import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Header, Footer } from '../../shared/components';

// API configuration - must be set via REACT_APP_API_BASE_URL environment variable
// Pattern matches admin app: API_BASE_URL is just the base domain, paths include /api/v1
const API_BASE = (process.env.REACT_APP_API_BASE_URL || '');
const apiUrl = (path) => `${API_BASE}${path}`;

if (!API_BASE) {
  console.error('REACT_APP_API_BASE_URL environment variable is required');
}

// Password Reset Form Component
function PasswordResetForm() {
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [tokenKind, setTokenKind] = useState(null); // 'user' | 'admin' after verify
  const [isVerifying, setIsVerifying] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      verifyToken(tokenFromUrl);
    }
  }, [searchParams]);

  const verifyToken = async (tokenToVerify) => {
    if (!API_BASE) {
      setMessage('Configuration error: API base URL is not set. Please contact support.');
      setMessageType('error');
      return;
    }

    setIsVerifying(true);
    setMessage('');

    const setInvalidToken = () => {
      setMessage('Invalid or expired reset token.');
      setMessageType('error');
      setTokenKind(null);
      setIsVerifying(false);
    };

    // 1) Try user/doctor reset token (from portal/login "Forgot password")
    try {
      const userRes = await axios.get(apiUrl(`/api/v1/auth/verify-reset-token/${encodeURIComponent(tokenToVerify)}`));
      if (userRes.data && userRes.data.valid) {
        setMessage('');
        setMessageType('');
        setTokenKind('user');
        setIsVerifying(false);
        return;
      }
    } catch (e) {
      // Not a user token; try admin below
    }

    // 2) Try admin reset token (from admin login "Forgot password")
    try {
      const adminRes = await axios.get(apiUrl(`/api/v1/auth/admin/password/verify-token/${encodeURIComponent(tokenToVerify)}`));
      if (adminRes.data && adminRes.data.valid) {
        setMessage('');
        setMessageType('');
        setTokenKind('admin');
        setIsVerifying(false);
        return;
      }
    } catch (e) {
      // Not admin token
    }

    setInvalidToken();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      setMessage('Reset token is required.');
      setMessageType('error');
      return;
    }

    if (newPassword.length < 8) {
      setMessage('Password must be at least 8 characters long.');
      setMessageType('error');
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match.');
      setMessageType('error');
      return;
    }

    if (!API_BASE) {
      setMessage('Configuration error: API base URL is not set. Please contact support.');
      setMessageType('error');
      return;
    }

    setIsLoading(true);
    setMessage('');

    const resetPayload = { token, new_password: newPassword };
    const resetUrl = tokenKind === 'admin'
      ? '/api/v1/auth/admin/password/reset'
      : '/api/v1/auth/reset-password';

    try {
      await axios.post(apiUrl(resetUrl), resetPayload);
      
      // Success - set password reset flag BEFORE setting message
      setIsPasswordReset(true);
      setMessage('Password has been reset successfully! You can now log in with your new password.');
      setMessageType('success');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Password reset error:', error);
      setIsPasswordReset(false); // Ensure flag is false on error
      if (error.response && error.response.data && error.response.data.detail) {
        setMessage(error.response.data.detail);
      } else if (error.request) {
        setMessage('Network error: Could not reach the server. Please check your connection.');
      } else {
        setMessage('An error occurred while resetting your password. Please try again.');
      }
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="reset-page-wrapper zivo-app-shell">
        <Header showDietitiansLink={false} variant="appShell" />
        <div className="zivo-app-shell-body">
          <aside className="zivo-app-shell-sidebar" aria-hidden="true" />
          <main className="zivo-app-shell-main">
        <div className="container">
          <div className="card">
            <div className="card-header">
              <div className="logo">ZivoHealth</div>
              <h1 className="title">Reset Password</h1>
              <p className="subtitle">Invalid reset link</p>
            </div>
            <div className="message error">
              No reset token found in the URL. Please use the link from your email.
            </div>
            <div className="card-footer">
              <button
                type="button"
                className="link"
                onClick={() => {
                  // Try to close the window/tab
                  try {
                    window.close();
                    // If window.close() doesn't work (user-opened tab), 
                    // redirect to a blank page
                    setTimeout(() => {
                      window.location.href = 'about:blank';
                    }, 100);
                  } catch (e) {
                    // Fallback: redirect to blank page
                    window.location.href = 'about:blank';
                  }
                }}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  padding: 0,
                  cursor: 'pointer',
                  font: 'inherit',
                  fontSize: 'inherit'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
          </main>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="reset-page-wrapper zivo-app-shell">
      <Header showDietitiansLink={false} variant="appShell" />
      <div className="zivo-app-shell-body">
        <aside className="zivo-app-shell-sidebar" aria-hidden="true" />
        <main className="zivo-app-shell-main">
      <div className="container">
        <div className="card">
          <div className="card-header">
            <div className="logo">ZivoHealth</div>
            <h1 className="title">Reset Password</h1>
            <p className="subtitle">
              {isPasswordReset ? 'Password reset successful!' : isVerifying ? 'Verifying link...' : 'Enter your new password below'}
            </p>
          </div>

        {message && (
          <div className={`message ${messageType}`}>
            {message}
          </div>
        )}

        {isPasswordReset ? (
          <>
            <div className="card-footer" style={{ marginTop: '20px' }}>
              <button
                type="button"
                className="button"
                onClick={() => {
                  // Try to close the window/tab
                  // This works if the window was opened by JavaScript
                  // For user-opened tabs, browsers may prevent this for security
                  try {
                    window.close();
                    // If window.close() doesn't work (user-opened tab), 
                    // try redirecting to a blank page or showing a message
                    setTimeout(() => {
                      // If still on the page after a short delay, the close didn't work
                      // Redirect to a blank page or show message
                      window.location.href = 'about:blank';
                    }, 100);
                  } catch (e) {
                    // Fallback: redirect to blank page
                    window.location.href = 'about:blank';
                  }
                }}
                style={{ width: '100%' }}
              >
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="label" htmlFor="newPassword">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  className="input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  minLength="8"
                />
              </div>

              <div className="form-group">
                <label className="label" htmlFor="confirmPassword">
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  className="input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  minLength="8"
                />
              </div>

              <button
                type="submit"
                className="button"
                disabled={isLoading || isVerifying || !tokenKind || !newPassword || !confirmPassword}
              >
                {isLoading && <span className="loading"></span>}
                {isLoading ? 'Resetting Password...' : isVerifying ? 'Verifying...' : 'Reset Password'}
              </button>
            </form>

            {!isLoading && (
              <div className="card-footer">
                <button
                  type="button"
                  className="link"
                  onClick={() => {
                    // Try to close the window/tab
                    try {
                      window.close();
                      // If window.close() doesn't work (user-opened tab), 
                      // redirect to a blank page
                      setTimeout(() => {
                        window.location.href = 'about:blank';
                      }, 100);
                    } catch (e) {
                      // Fallback: redirect to blank page
                      window.location.href = 'about:blank';
                    }
                  }}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    padding: 0,
                    cursor: 'pointer',
                    font: 'inherit',
                    fontSize: 'inherit'
                  }}
                >
                  Close
                </button>
              </div>
            )}
          </>
        )}
        </div>
      </div>
        </main>
      </div>
      <Footer />
    </div>
  );
}

// Main App Component
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/reset-password" element={<PasswordResetForm />} />
        <Route path="/" element={<PasswordResetForm />} />
      </Routes>
    </Router>
  );
}

export default App;
